import {DataFieldType} from '@frontapp/workflows/data_fields_table';
import {MacroInputData, MacroInputDefinition, MacroInputId} from '@frontapp/workflows/macros';
import {isUndefined} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  deserializeDataFieldsTableResourceValue,
  serializeDataFieldsTableResourceValue,
} from '../../../../dataTable/helpers';
import {MacroInputBoolean} from './inputs/macroInputBoolean';
import {MacroInputDateTime} from './inputs/macroInputDateTime';
import {MacroInputDropdown} from './inputs/macroInputDropdown';
import {MacroInputInbox} from './inputs/macroInputInbox';
import {MacroInputNumber} from './inputs/macroInputNumber';
import {MacroInputString} from './inputs/macroInputString';
import {MacroInputTeammate} from './inputs/macroInputTeammate';

/*
 * Props.
 */

export interface MacroInputProps {
  macroInputs: ReadonlyArray<MacroInputDefinition>;
  values: Partial<MacroInputData>;
  onValuesChange: (id: MacroInputId, newValue: string | undefined) => void;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/*
 * Component.
 */

export const MacroInput: FC<MacroInputProps> = ({macroInputs, values, onValuesChange}) => {
  const renderMacroInputType = (macroInput: MacroInputDefinition): ReactNode => {
    const inputValue = values[macroInput.id];

    switch (macroInput.type) {
      case DataFieldType.STRING: {
        if (isDefined(macroInput.enum)) {
          const validatedEnumValue =
            isDefined(inputValue) && macroInput.enum.includes(inputValue) ? inputValue : undefined;

          return (
            <MacroInputDropdown<string>
              value={validatedEnumValue}
              label={macroInput.name}
              description={macroInput.description}
              onChange={(newValue) => onValuesChange(macroInput.id, newValue)}
              dropdownItems={macroInput.enum.map((enumValue) => ({label: enumValue, value: enumValue}))}
              renderValue={(value) => value}
            />
          );
        }

        return (
          <MacroInputString
            value={inputValue}
            label={macroInput.name}
            description={macroInput.description}
            onChange={(newValue) => onValuesChange(macroInput.id, newValue)}
          />
        );
      }
      case DataFieldType.DATETIME: {
        const maybeDateTime = isDefined(inputValue) ? DateTime.fromISO(inputValue) : undefined;
        const value = maybeDateTime?.isValid ? maybeDateTime.toMillis().toString() : undefined;

        return (
          <MacroInputDateTime
            value={value}
            label={macroInput.name}
            description={macroInput.description}
            onChange={(newValue) => {
              if (isUndefined(newValue)) {
                onValuesChange(macroInput.id, undefined);
                return;
              }

              const newDateTime = DateTime.fromMillis(parseInt(newValue, 10));
              onValuesChange(macroInput.id, newDateTime.isValid ? newDateTime.toISO() : undefined);
            }}
          />
        );
      }

      case DataFieldType.NUMBER: {
        const value = isDefined(inputValue) ? parseInt(inputValue, 10) : undefined;

        return (
          <MacroInputNumber
            value={isDefined(value) && isFinite(value) ? value : undefined}
            label={macroInput.name}
            description={macroInput.description}
            onChange={(newValue) => onValuesChange(macroInput.id, newValue?.toString())}
          />
        );
      }

      case DataFieldType.BOOLEAN: {
        const value = isDefined(inputValue) ? inputValue === 'true' : undefined;

        return (
          <MacroInputBoolean
            value={value}
            label={macroInput.name}
            description={macroInput.description}
            onChange={(newValue) => {
              if (isUndefined(newValue)) {
                onValuesChange(macroInput.id, undefined);
                return;
              }

              onValuesChange(macroInput.id, newValue ? 'true' : 'false');
            }}
          />
        );
      }
      case DataFieldType.TEAMMATE: {
        const teammateId = deserializeDataFieldsTableResourceValue(inputValue, 'teammate');

        return (
          <MacroInputTeammate
            value={teammateId}
            label={macroInput.name}
            description={macroInput.description}
            onChange={(newValue) =>
              onValuesChange(
                macroInput.id,
                serializeDataFieldsTableResourceValue(newValue ?? null, 'teammate'),
              )
            }
          />
        );
      }
      case DataFieldType.INBOX: {
        const inboxId = deserializeDataFieldsTableResourceValue(inputValue, 'inbox');

        return (
          <MacroInputInbox
            value={inboxId}
            label={macroInput.name}
            description={macroInput.description}
            onChange={(newValue) =>
              onValuesChange(macroInput.id, serializeDataFieldsTableResourceValue(newValue ?? null, 'inbox'))
            }
          />
        );
      }
      default:
        return null;
    }
  };
  return (
    <StyledWrapperDiv>
      {macroInputs.map((macroInput) => (
        <React.Fragment key={macroInput.id}>{renderMacroInputType(macroInput)}</React.Fragment>
      ))}
    </StyledWrapperDiv>
  );
};
